import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Building2, Factory, Send, Clock, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const locations = [
  {
    type: "Oficinas",
    icon: Building2,
    address: "Avenida Santa Catalina 82",
    city: "03725 Teulada (Alicante)",
    country: "España",
  },
  {
    type: "Domicilio Industrial",
    icon: Factory,
    address: "Calle Benz 10 Nave 3",
    extra: "Polígono Industrial La Pedrera",
    city: "03720 Benissa (Alicante)",
    country: "España",
  },
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Por favor, completa los campos obligatorios");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, formData);
      toast.success("Mensaje enviado correctamente", {
        description: "Nos pondremos en contacto contigo pronto."
      });
      setFormData({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (err) {
      console.error("Error sending contact:", err);
      toast.error("Error al enviar el mensaje. Inténtalo de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen" data-testid="contact-page">
      {/* Hero */}
      <section className="relative py-32 pt-40 overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <span className="text-udl-primary text-xs font-mono uppercase tracking-widest mb-4 block">
              Contacto
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-udl-white mb-6">
              Hablemos de tu <span className="text-udl-primary">proyecto</span>
            </h1>
            <p className="text-udl-gray-400 text-lg leading-relaxed">
              ¿Tienes una idea para tu bebida? Ponte en contacto con nosotros y te ayudamos 
              a hacerla realidad. Estamos en Alicante, España.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="py-20 bg-udl-surface">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-udl-white mb-8">
                Información de contacto
              </h2>

              {/* Locations */}
              <div className="space-y-6 mb-10">
                {locations.map((loc, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-12 h-12 bg-udl-surface flex items-center justify-center flex-shrink-0">
                      <loc.icon className="text-udl-primary" size={20} />
                    </div>
                    <div>
                      <p className="text-udl-primary text-sm font-bold uppercase tracking-wider mb-1">
                        {loc.type}
                      </p>
                      <p className="text-udl-white">{loc.address}</p>
                      {loc.extra && <p className="text-udl-gray-400 text-sm">{loc.extra}</p>}
                      <p className="text-udl-gray-400 text-sm">{loc.city}</p>
                      <p className="text-udl-gray-400 text-sm">{loc.country}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Phone */}
              <div className="flex gap-4 mb-6">
                <div className="w-12 h-12 bg-udl-surface flex items-center justify-center flex-shrink-0">
                  <Phone className="text-udl-primary" size={20} />
                </div>
                <div>
                  <p className="text-udl-primary text-sm font-bold uppercase tracking-wider mb-1">
                    Teléfono
                  </p>
                  <a 
                    href="tel:+34601645533" 
                    className="text-udl-white hover:text-udl-primary transition-colors"
                    data-testid="phone-link"
                  >
                    +34 601 64 55 33
                  </a>
                </div>
              </div>

              {/* Email - Ofuscado para evitar spam */}
              <div className="flex gap-4 mb-10">
                <div className="w-12 h-12 bg-udl-surface flex items-center justify-center flex-shrink-0">
                  <Mail className="text-udl-primary" size={20} />
                </div>
                <div>
                  <p className="text-udl-primary text-sm font-bold uppercase tracking-wider mb-1">
                    Email
                  </p>
                  <p className="text-udl-white" data-testid="email-display">
                    info<span className="font-mono text-udl-primary mx-0.5">[arroba]</span>uniquedrinkslab.com
                  </p>
                  <p className="text-udl-gray-700 text-xs mt-1">
                    (escribe @ en lugar de [arroba])
                  </p>
                </div>
              </div>

              {/* Hours */}
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-udl-surface flex items-center justify-center flex-shrink-0">
                  <Clock className="text-udl-primary" size={20} />
                </div>
                <div>
                  <p className="text-udl-primary text-sm font-bold uppercase tracking-wider mb-1">
                    Horario
                  </p>
                  <p className="text-udl-white">Lunes - Viernes</p>
                  <p className="text-udl-gray-400 text-sm">9:00 - 18:00 CET</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-udl-card border border-udl-gray-800 p-8">
              <h2 className="font-heading text-2xl font-bold text-udl-white mb-6">
                Envíanos un mensaje
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-udl-gray-400 mb-2 block">Nombre *</Label>
                    <Input 
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="bg-udl-surface border-udl-gray-800 text-udl-white"
                      placeholder="Tu nombre"
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-udl-gray-400 mb-2 block">Email *</Label>
                    <Input 
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="bg-udl-surface border-udl-gray-800 text-udl-white"
                      placeholder="tu@email.com"
                      required
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-udl-gray-400 mb-2 block">Teléfono</Label>
                    <Input 
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="bg-udl-surface border-udl-gray-800 text-udl-white"
                      placeholder="+34 600 000 000"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <Label className="text-udl-gray-400 mb-2 block">Empresa</Label>
                    <Input 
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                      className="bg-udl-surface border-udl-gray-800 text-udl-white"
                      placeholder="Nombre de tu empresa"
                      data-testid="contact-company-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-udl-gray-400 mb-2 block">Mensaje *</Label>
                  <Textarea 
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="bg-udl-surface border-udl-gray-800 text-udl-white min-h-[150px]"
                    placeholder="Cuéntanos sobre tu proyecto de bebida..."
                    required
                    data-testid="contact-message-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="contact-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Enviar mensaje
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps */}
      <section className="h-[450px] bg-udl-surface relative" data-testid="map-section">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d49892.89876543!2d0.0789!3d38.7289!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x129e1e8b7b3b3b3b%3A0x1234567890abcdef!2sTeulada%2C%20Alicante%2C%20Spain!5e0!3m2!1sen!2ses!4v1234567890"
          width="100%"
          height="100%"
          style={{ border: 0, filter: "grayscale(100%) invert(92%) contrast(90%)" }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Ubicación Unique Drinks Lab - Teulada, Alicante"
        />
        <div className="absolute bottom-4 left-4 bg-udl-bg/90 backdrop-blur-sm border border-udl-gray-800 p-4">
          <div className="flex items-center gap-2 text-udl-primary mb-1">
            <MapPin size={16} />
            <span className="font-heading font-bold text-sm">Costa Blanca Norte</span>
          </div>
          <p className="text-udl-gray-400 text-xs">Teulada · Benissa · Alicante</p>
        </div>
      </section>

      <Footer />
    </main>
  );
}
